/**
 * A banked TestIt result, read for a person to see — the ONE reading every
 * surface shares: the citizen's TestIt page (its cards, its result dialog, the
 * attempt history), the company's Vetting drawer (the TestIt tab's rows, the
 * per-test sheet, the CheckUp side's languages) and a decision record's
 * evidence. Each of them used to read the stored payload on its own: the
 * drawer drew all four CEFR skills, the citizen saw only the overall band, and
 * a summary line was built in a component with "CEFR " glued on in English.
 *
 * Reading only. Whether a result is valid, reusable or expired is the server's
 * (`TalentTestResultView.isValid` / `isReusable` / `daysRemaining`); nothing
 * here decides it. The payload shapes are the store's (`talent_test_results.
 * result`, see `test-catalog.ts`): a language carries `cefr` (overall + up to
 * four skills), an optional analyser `confidence` (0–1) and `unverified` when
 * self-declared; the pitch its `duration` in seconds; the questionnaire its
 * `questionsAnswered`.
 *
 * The lines are translated here, with literal keys, so the i18n key-usage gate
 * checks every one of them against en.json.
 *
 * @module heroes/checkup
 */
import type { TFunction } from 'i18next';

/** The four CEFR skills a language result can state, in the order they are drawn. */
export const CEFR_SKILLS = ['reading', 'listening', 'speaking', 'writing'] as const;
export type CefrSkill = (typeof CEFR_SKILLS)[number];

/** A result payload as the store keeps it — any shape; only known fields are read. */
type ResultPayload = Readonly<Record<string, unknown>> | null | undefined;

/** The slice of a banked result (`TalentTestResultView`) this module reads. */
export interface BankedTestResult {
  /** The widget that administered it — `language-test`, `pitch-recorder`, `motivational`. */
  readonly widgetId: string;
  readonly result: ResultPayload;
  /** The store's one-line summary (a CEFR band), when it has one. */
  readonly score: string | null;
  /** `checkup` | `testit` — where the result was taken. */
  readonly source: string;
}

/** A language result, read. */
export interface CefrReading {
  /** The overall band (`B2`, `Native`); null when none was stated. */
  readonly overall: string | null;
  /** Each skill's band; null for a skill that was not assessed. */
  readonly skills: Readonly<Record<CefrSkill, string | null>>;
  /** Self-declared — a level the person stated, with nothing analysed behind it. */
  readonly unverified: boolean;
  /** The analyser's confidence in the certificate, 0–1; null when it gave none. */
  readonly confidence: number | null;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function band(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

export function readCefrResult(payload: ResultPayload): CefrReading {
  const cefr = isRecord(payload?.['cefr']) ? payload['cefr'] : {};
  const confidence = payload?.['confidence'];
  return {
    overall: band(cefr['overall']),
    skills: {
      reading: band(cefr['reading']),
      listening: band(cefr['listening']),
      speaking: band(cefr['speaking']),
      writing: band(cefr['writing']),
    },
    unverified: payload?.['unverified'] === true,
    confidence: typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : null,
  };
}

/** The pitch's length in seconds — null when unknown (a stored 0 means unknown, not a 0 s clip). */
export function readPitchDuration(payload: ResultPayload): number | null {
  const duration = payload?.['duration'];
  return typeof duration === 'number' && Number.isFinite(duration) && duration > 0
    ? duration
    : null;
}

/** How many questions the questionnaire was answered with — null when the payload does not say. */
export function readQuestionsAnswered(payload: ResultPayload): number | null {
  const answered = payload?.['questionsAnswered'];
  return typeof answered === 'number' && Number.isInteger(answered) && answered >= 0
    ? answered
    : null;
}

/** A clip's length as a clock reads it — `1:02`. Digits and a colon, the same in every language. */
export function formatClipDuration(seconds: number): string {
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/**
 * A language result, said: the band, what it is, each skill named, and what
 * stands behind it — "Self-declared", or the analyser's confidence. The props
 * of `CefrBreakdown` (@tonnie/ui), so every surface draws a language from the
 * same words.
 */
export function cefrBreakdownLines(
  reading: CefrReading,
  t: TFunction,
  formatPercent: (value: number) => string
): {
  overall: string | null;
  overallLabel: string;
  overallName: string | null;
  skills: ReadonlyArray<{ id: CefrSkill; label: string; level: string | null }>;
  selfDeclaredLabel?: string;
  confidenceLabel?: string;
} {
  const label: Record<CefrSkill, string> = {
    reading: t('testit.result.skillReading'),
    listening: t('testit.result.skillListening'),
    speaking: t('testit.result.skillSpeaking'),
    writing: t('testit.result.skillWriting'),
  };
  return {
    overall: reading.overall,
    overallLabel: t('testit.result.overallLevel'),
    overallName: reading.overall ? cefrLevelName(reading.overall, t) : null,
    skills: CEFR_SKILLS.map((id) => ({ id, label: label[id], level: reading.skills[id] })),
    // A declaration has no analyser behind it: its confidence is never said.
    selfDeclaredLabel: reading.unverified ? t('testit.result.selfDeclared') : undefined,
    confidenceLabel:
      !reading.unverified && reading.confidence != null
        ? t('testit.result.analyserConfidence', {
            value: formatPercent(reading.confidence * 100),
          })
        : undefined,
  };
}

/**
 * The citizen-facing name of a CEFR band ("Upper Intermediate" for B2), in the
 * viewer's language — null for a band this does not name. Literal `t()`
 * calls, so the key-usage gate sees every key. Every surface names a band from here: the CheckUp's language widget, the
 * citizen's TestIt cards and dialog.
 */
export function cefrLevelName(level: string, t: TFunction): string | null {
  switch (level.toUpperCase()) {
    case 'A1':
      return t('checkup.languageTests.cefrLevel.a1');
    case 'A2':
      return t('checkup.languageTests.cefrLevel.a2');
    case 'B1':
      return t('checkup.languageTests.cefrLevel.b1');
    case 'B2':
      return t('checkup.languageTests.cefrLevel.b2');
    case 'C1':
      return t('checkup.languageTests.cefrLevel.c1');
    case 'C2':
      return t('checkup.languageTests.cefrLevel.c2');
    case 'NATIVE':
      return t('checkup.languageTests.cefrLevel.native');
    default:
      return null;
  }
}

/** One line for a result — a list row, an attempt in the history, a record's evidence. */
export function testResultSummary(result: BankedTestResult, t: TFunction): string {
  switch (result.widgetId) {
    case 'language-test': {
      const overall = readCefrResult(result.result).overall;
      return overall
        ? t('testit.result.cefrLevel', { level: overall })
        : t('testit.result.recorded');
    }
    case 'pitch-recorder': {
      const duration = readPitchDuration(result.result);
      return duration != null
        ? t('testit.result.recordedDuration', { duration: formatClipDuration(duration) })
        : t('testit.result.recorded');
    }
    case 'motivational': {
      const answered = readQuestionsAnswered(result.result);
      return answered != null
        ? t('testit.result.motivationalAnswered', { count: answered })
        : t('testit.result.motivationalComplete');
    }
    default:
      return result.score ?? t('testit.result.recorded');
  }
}

/** Where a result was taken — in a CheckUp, or on its own in TestIt. */
export function testResultSource(result: Pick<BankedTestResult, 'source'>, t: TFunction): string {
  return result.source === 'checkup'
    ? t('testit.result.sourceCheckup')
    : t('testit.result.sourceTestit');
}

/**
 * What stands behind a result: a self-declared level says so first (it has no
 * test behind it, wherever it was recorded); otherwise where it was taken.
 */
export function testResultProvenance(
  result: Pick<BankedTestResult, 'source' | 'result'>,
  t: TFunction
): string {
  return result.result?.['unverified'] === true
    ? t('testit.result.selfDeclared')
    : testResultSource(result, t);
}
